/**
 * Every user-visible string, in Norwegian bokmål.
 *
 * Kept in one place so the copy reads consistently — the app talks to the user in
 * one voice, and a stray English label in a Norwegian interface is jarring.
 */

export const t = {
  appName: 'Øya annotated',
  appTagline: 'Programmet, med dine notater',

  nav: {
    back: 'Tilbake',
    toProgram: 'Til programmet',
    /*
     * The mark's name away from the day view, where there is no wordmark beside it to
     * say what it is. «Hjem» would be a guess about where that lands; this says the
     * thing it actually does, which is the day you are standing in.
     */
    today: 'Til dagens program',
    friends: 'Venner',
    /*
     * The count sits in the accessible name, not only in the dot.
     *
     * A badge that is a coloured circle says «noen venter på deg» to a reader who can
     * see it and nothing whatsoever to one who cannot — and a friend request is the
     * one thing in this app that goes nowhere until it is answered (R31).
     */
    friendsWithRequests: (n: number) =>
      n === 1 ? 'Venner, 1 ny forespørsel' : `Venner, ${n} nye forespørsler`,
    import: 'Importer fra Broadcast',
    map: 'Kart',
    skipToContent: 'Hopp til innholdet',
  },

  map: {
    title: 'Festivalkart',
    /*
     * The alt text names what somebody who cannot see the map would otherwise have to
     * open it to learn: that it is the site plan, and which of the six stages are on
     * it. The map itself is a picture with baked-in labels, so nothing in it is
     * reachable any other way.
     */
    alt: 'Kart over festivalområdet på Tøyen, med scenene Amfiet, Sirkus, Vindfruen, Hagen til Stø, Klubben og Biblioteket, samt innganger, mat, barer, toaletter og førstehjelp.',
    hint: 'Knip for å zoome, dra for å flytte.',
    zoomIn: 'Zoom inn',
    zoomOut: 'Zoom ut',
    reset: 'Vis hele kartet',
    // Whose map this is, on the page that shows it. It is Øyafestivalen's artwork,
    // taken from their own map page, and the credit links back to where it lives.
    credit: 'Kartet er laget av Øyafestivalen',
    source: 'Se kartet på oyafestivalen.no',
  },

  broadcast: {
    title: 'Importer fra Broadcast',
    // Says what it will do before it does it: the import writes to the same thumbs the
    // user sets by hand, and nobody should have to try it to find that out.
    help: 'Lim inn lenken til planen din. Konsertene i den får tommel opp.',
    urlLabel: 'Lenke til Broadcast-planen',
    urlPlaceholder: 'https://brdca.st/schedule/…',
    submit: 'Importer',
    importing: 'Henter planen …',
    close: 'Lukk',
    done: (n: number) =>
      n === 1 ? '1 konsert fikk tommel opp' : `${n} konserter fikk tommel opp`,
    // Broadcast covers Øyanatt and the club programme, which this app leaves out, so a
    // schedule with entries we cannot place is normal — and naming them is the
    // difference between an honest count and a silent partial import.
    skipped: (names: readonly string[]) =>
      `Ikke i programmet her: ${names.join(', ')}`,
    failedLink: 'Det ser ikke ut som en lenke til en Broadcast-plan.',
    failedMissing: 'Fant ingen konserter på den lenken.',
    failedFetch: 'Klarte ikke å hente planen. Sjekk nettet og prøv igjen.',
  },

  day: {
    // The filter keeps a thumbs-up *or* a terningkast of four or more, so «Tommel
    // opp» would name half of what it does. «Likt» covers both signals and still
    // fits the chip beside its count.
    onlyLiked: 'Bare likte konserter',
    onlyLikedShort: 'Likt',
    nowLabel: 'Nå',
    nowAria: 'Nåværende tidspunkt',
    stageFilterLabel: 'Filtrer på scene',
    allStages: 'Alle scener',
    emptyFiltered: 'Ingen likte konserter denne dagen ennå.',
    emptyStages: 'Ingen konserter på de valgte scenene denne dagen.',
    emptyLikedStages: 'Ingen likte konserter på de valgte scenene.',
    emptyFilteredAction: 'Vis alle konsertene',
    empty: 'Ingen konserter denne dagen.',
    concertCount: (n: number) => `${n} ${n === 1 ? 'konsert' : 'konserter'}`,
    concertCountFiltered: (shown: number, total: number) =>
      `${shown} av ${total} ${total === 1 ? 'konsert' : 'konserter'}`,
    /*
     * What a row is not showing you.
     *
     * A friend's annotations reach the schedule for every concert they have annotated,
     * so a much-loved concert can carry more friends than a phone row has any business
     * holding. The row keeps two and says how many are left (R33). Phrased as a
     * continuation — «Anne: …», «Bjørn: …», «og 3 venner til» — because that is what it
     * is: the same list, cut short. Tapping the row shows all of them.
     */
    moreFriends: (n: number) => (n === 1 ? 'og 1 venn til' : `og ${n} venner til`),
    ended: 'Ferdig',
    live: 'Spilles nå',
    upcoming: 'Kommer',
  },

  search: {
    label: 'Søk etter artist',
    // Names the reach, because the field sits under the day tabs and would
    // otherwise read as a filter on the day showing.
    placeholder: 'Søk i hele programmet …',
    results: 'Søkeresultater',
    empty: (query: string) => `Ingen treff på «${query}»`,
    clear: 'Tøm søket',
  },

  concert: {
    thumbsUp: 'Tommel opp',
    thumbsDown: 'Tommel ned',
    thumbsClear: 'Fjern vurdering',
    ratingLabel: 'Din vurdering',
    // «Terningkast» is the standard Norwegian 1–6 review idiom, which is exactly what
    // the dice depict — so the control needs no explaining.
    diceLabel: 'Terningkast',
    diceValue: (n: number) => `Terningkast ${n}`,
    diceClear: 'Fjern terningkast',
    noteLabel: 'Ditt notat',
    notePlaceholder: 'Skriv et notat til deg selv …',
    noteSaved: 'Lagret',
    noteSaving: 'Lagrer …',
    noteEphemeral: 'Notatene lagres ikke på denne enheten',
    noteEphemeralHelp:
      'Nettleseren blokkerer lagring, så notatene forsvinner når du lukker appen. Prøv å åpne appen utenfor privat nettlesing.',
    share: 'Del med en venn',
    // Read out after the clock slot, so the link announces as «13:50–14:35, vis i
    // dagsprogrammet». The digits on their own say nothing about where tapping goes.
    timeLink: 'vis i dagsprogrammet',
    // Reads straight into the list of names that follows: «Samtidig som Anakin
    // Justin, ARY». Not «Kolliderer med» — the app states what is on, and leaves
    // the choice to the reader.
    simultaneous: 'Samtidig som',
    genres: 'Sjangere',
    notFound: 'Fant ikke konserten',
    notFoundHelp: 'Lenken kan være gammel, eller konserten kan ha blitt fjernet fra programmet.',
  },

  friends: {
    title: 'Venner',

    /*
     * The add form. Every string here says «forespørsel» rather than «venn lagt til»,
     * because that is what actually happens: adding someone proposes a friendship and
     * grants nothing until they answer (A8, R29). Copy that claimed otherwise would be
     * the only place in the app where the user is told they got something they did not.
     */
    addTitle: 'Legg til en venn',
    addHelp:
      'Vi sender forespørselen på e-post. Dere ser notatene til hverandre først når den er godtatt.',
    emailLabel: 'Vennens e-postadresse',
    emailPlaceholder: 'venn@example.com',
    add: 'Send forespørsel',
    sending: 'Sender …',
    sent: 'Forespørselen er sendt på e-post. Du ser notatene når den er godtatt.',
    /*
     * The same request, with nobody told about it: no SMTP configured, or a send that
     * failed. It still stands and still works — but the person it names finds it only
     * by opening the app, and the sender is the only one who can hurry that along.
     */
    sentSilent: 'Forespørselen er sendt. Vennen din ser den neste gang de logger inn.',
    failed: 'Klarte ikke å sende forespørselen. Prøv igjen.',
    // Named apart from the generic failure because the cap is something the user can
    // simply wait out, and «prøv igjen» would send them straight back into it.
    rateLimited: 'Du har sendt mange forespørsler i dag. Prøv igjen i morgen.',

    incomingTitle: 'Forespørsler til deg',
    /*
     * R27, placed on the request itself rather than in a policy paragraph nobody
     * reads. Accepting hands over everything already written and everything written
     * later, in both directions, and the sentence has to be in front of the button
     * that does it.
     */
    acceptDiscloses:
      'Godtar du, ser denne personen alt du noterer — også det du skriver senere. Du ser like mye av deres.',
    accept: 'Godta',
    decline: 'Avslå',
    declineConfirm: 'Avslå for godt',
    declineWarning:
      'Avslår du, forsvinner forespørselen og personen kan ikke spørre igjen. Du kan fortsatt legge dem til selv.',

    outgoingTitle: 'Sendt av deg',
    awaitingReply: 'Venter på svar',
    cancelRequest: 'Trekk tilbake',

    friendsTitle: 'Vennene dine',
    remove: 'Fjern venn',
    removeConfirm: 'Ja, fjern',
    removeWarning:
      'Fjerner du vennen, mister dere begge tilgang til notatene til hverandre — også det som allerede er delt.',
    // The attribution on an exchanged annotation. Short on purpose: it sits under a
    // concert name on a phone row, where a whole sentence would wrap.
    by: (name: string) => `Fra ${name}`,
    byYou: 'Fra deg',
    // Friendship shows everything a friend writes; this marks the ones they picked out
    // and sent, so the deliberate act stays visible among them (R34).
    explicitlyShared: 'Delt med deg',
    noEntries: 'Ingen notater ennå.',

    sharedTitle: 'Delt med deg av andre',
    sharedHelp: 'Konserter noen har delt med deg uten at dere er venner.',

    empty: 'Du har ingen venner her ennå.',
    emptyHelp:
      'Legg til noen på e-postadressen deres. Dere ser notatene til hverandre når begge har sagt ja.',
    signInHelp: 'Du må logge inn med Google for å se og legge til venner.',
    localOnly: 'Denne appen er satt opp uten sky, så venner er ikke tilgjengelig her.',
    actionFailed: 'Det gikk ikke. Prøv igjen.',
    cancel: 'Avbryt',
  },

  shared: {
    from: (name: string) => `${name} delte denne med deg`,
    loading: 'Henter notatet …',
    failed: 'Kunne ikke hente notatet',
    retry: 'Prøv igjen',
    signInToSee: 'Logg inn for å se notatet',
    signInHelp: 'Du må logge inn med Google for å se hva vennen din skrev.',
  },

  install: {
    iosTitle: 'Legg til på Hjem-skjerm',
    iosBody:
      'Trykk på Del-knappen nederst i Safari, og velg «Legg til på Hjem-skjerm». Da får du appen på telefonen — og kan motta varsler fra venner.',
    androidTitle: 'Installer appen',
    androidBody: 'Legg Øya annotated på hjem-skjermen for raskere tilgang og varsler.',
    androidAction: 'Installer',
    dismiss: 'Ikke nå',
  },

  auth: {
    signIn: 'Logg inn med Google',
    /*
     * The same offer, shortened for the top bar.
     *
     * «Logg inn med Google» does not fit beside a wordmark and two icon buttons at
     * 390px, and truncating the one control that gets a signed-out user anywhere is a
     * worse answer than shortening it on purpose. It stays a prefix of `signIn`, which
     * remains the button's accessible name — so the provider is still announced, and
     * the visible words are contained in the name a voice command would use (WCAG
     * 2.5.3, «Label in Name»).
     */
    signInShort: 'Logg inn',
    signOut: 'Logg ut',
    signedInAs: (name: string) => `Logget inn som ${name}`,
    localOnly: 'Notatene dine lagres bare på denne enheten',
    deleteData: 'Slett dataene mine',
    deleteConfirm: 'Ja, slett alt',
    deleteCancel: 'Avbryt',
    deleteWarning:
      'Dette sletter alle vurderingene og notatene dine, også de du har delt med andre. Det kan ikke angres.',
    signInFailed: 'Innloggingen kunne ikke starte.',
    dismissError: 'Lukk feilmeldingen',
    // auth/unauthorized-domain is a configuration error, not something the user can
    // do anything about, so it is named plainly instead of dressed up as a retry.
    signInUnauthorizedDomain:
      'Denne adressen er ikke godkjent for innlogging. Legg den til som godkjent domene i Firebase (steg 7 i scripts/setup-gcp.sh).',
  },

  share: {
    emailLabel: 'Vennens e-postadresse',
    emailPlaceholder: 'venn@example.com',
    send: 'Send',
    sending: 'Sender …',
    sentPush: 'Sendt som varsel',
    sentEmail: 'Sendt på e-post',
    sentLink: 'Kopier lenken og send den selv',
    // R27, in the other place the consequence lands. Sending a concert on e-post also
    // proposes a friendship, and the sender should learn that here rather than from a
    // row appearing on someone else's friends page.
    alsoProposes:
      'Å sende konserten foreslår også et vennskap. Godtar vennen din, ser dere notatene til hverandre.',
    failed: 'Klarte ikke å sende. Prøv igjen, eller kopier lenken nedenfor.',
    signInToShare: 'Logg inn for å sende konserten til en venn.',
    linkOnly: 'Kopier lenken og send den til hvem du vil.',
    copyLink: 'Kopier lenke',
    copied: 'Lenken er kopiert',
  },

  notifications: {
    offer: 'Få et varsel når en venn deler en konsert med deg.',
    enable: 'Slå på varsler',
    enabling: 'Slår på …',
    failed: 'Klarte ikke å slå på varsler. Prøv igjen senere.',
    needsInstallTitle: 'Legg appen til på Hjem-skjerm først.',
    needsInstallBody:
      'iPhone gir bare varsler til apper som ligger på hjem-skjermen. Trykk Del-knappen i Safari og velg «Legg til på Hjem-skjerm».',
    deniedTitle: 'Varsler er blokkert.',
    deniedBody:
      'Du har sagt nei til varsler for denne appen, og nettsider kan ikke spørre på nytt. Slå det på igjen under Innstillinger → Varsler hvis du ombestemmer deg.',
  },

  offline: {
    banner: 'Du er offline — programmet og notatene dine fungerer fortsatt',
  },

  errors: {
    generic: 'Noe gikk galt',
    reload: 'Last inn på nytt',
  },
} as const
